import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * Interface representing the state for the Image-to-Image feature
 * Manages the state of the original image, generated image, and related metadata
 */
interface ImageToImageState {
  /** Name of the current file being processed */
  fileName: string;
  
  /** URL path to the generated image */
  generatedImageUrl: string;
  
  /** Currently selected style for image transformation */
  selectedStyle: string;
  
  /** Flag indicating if the current image has been saved */
  isSaved: boolean;
  
  /** 
   * Original image file data in base64 format
   * Stored as string to avoid Redux serialization issues
   */
  originalImageBase64: string | null;
  
  /** Original image file metadata */
  originalImageInfo: {
    name: string;
    size: number;
    type: string;
    lastModified: number;
  } | null;
}

/**
 * Initial state for the Image-to-Image feature
 */
const initialState: ImageToImageState = {
  fileName: 'Untitled1',
  generatedImageUrl: '',
  selectedStyle: 'ghibli',
  isSaved: false,
  originalImageBase64: null,
  originalImageInfo: null,
};

/**
 * Payload type for saving generated image information
 */
interface SaveGeneratedImagePayload {
  generatedImageUrl: string;
  selectedStyle: string;
}

/**
 * Payload type for saving file status
 */
interface SaveFilePayload {
  fileSaved: boolean;
}

/**
 * Payload type for setting file name
 */
interface SetFileNamePayload {
  fileName: string;
}

/**
 * Payload type for setting original image
 */
interface SetOriginalImagePayload {
  file: File | null;
  base64: string | null;
}

/**
 * Redux slice for the Image-to-Image feature
 * Handles all state changes related to image transformation
 */
const imageToImageSlice = createSlice({
  name: 'imageToImage',
  initialState,
  reducers: {
    /**
     * Saves information about the generated image and selected style
     * Called after successful image generation
     */
    saveGeneratedImage: (state, action: PayloadAction<SaveGeneratedImagePayload>) => {
      state.generatedImageUrl = action.payload.generatedImageUrl;
      state.selectedStyle = action.payload.selectedStyle;
    },
    
    /**
     * Updates the saved status of the current image
     * Used to track if the image has been saved to the user's account
     */
    saveFile: (state, action: PayloadAction<SaveFilePayload>) => {
      state.isSaved = action.payload.fileSaved;
    },
    
    /**
     * Sets the file name for the current image
     * Used when uploading a new image or renaming an existing one
     */
    setFileName: (state, action: PayloadAction<SetFileNamePayload>) => {
      state.fileName = action.payload.fileName;
    },
    
    /**
     * Sets information about the original uploaded image
     * Stores both metadata and base64 representation of the file
     * Can accept null to clear the current image
     */
    setOriginalImage: (state, action: PayloadAction<SetOriginalImagePayload>) => {
      if (action.payload.file) {
        state.originalImageInfo = {
          name: action.payload.file.name,
          size: action.payload.file.size,
          type: action.payload.file.type,
          lastModified: action.payload.file.lastModified,
        };
        state.originalImageBase64 = action.payload.base64;
      } else {
        state.originalImageInfo = null;
        state.originalImageBase64 = null;
      }
    },
    
    /**
     * Resets the state to initial values
     * Used when starting a new image transformation session
     */
    resetState: (state) => {
      state.fileName = initialState.fileName;
      state.generatedImageUrl = initialState.generatedImageUrl;
      state.selectedStyle = initialState.selectedStyle;
      state.isSaved = initialState.isSaved;
      state.originalImageBase64 = initialState.originalImageBase64;
      state.originalImageInfo = initialState.originalImageInfo;
    },
  },
});

// Export actions for use in components
export const {
  saveGeneratedImage,
  saveFile,
  setFileName,
  setOriginalImage,
  resetState,
} = imageToImageSlice.actions;

// Export the reducer for store configuration
export default imageToImageSlice.reducer; 